import { apiFetch, apiState } from "./client.js";
import { ENDPOINTS } from "./endpoints.js";
import { ApiError } from "./errors.js";
import type { MeUser } from "./me.js";

export type EmailCodePurpose = "register" | "login" | "reset-password";

export type EmailCodeRequest = {
  email: string;
  purpose?: EmailCodePurpose;
};

export type EmailCodeResponse = {
  email: string;
  expiresInSeconds: number;
  retryAfterSeconds?: number;
};

export type RegisterRequest = {
  username: string;
  email: string;
  verificationCode: string;
  password: string;
  displayName: string;
};

export type LoginRequest = {
  username: string;
  password: string;
};

export type ChangePasswordRequest = {
  currentPassword: string;
  newPassword: string;
};

export type AuthSession = {
  token: string;
  user: MeUser;
};

export async function requestEmailCode(input: EmailCodeRequest): Promise<EmailCodeResponse> {
  return apiFetch<EmailCodeResponse>(ENDPOINTS.authEmailCode, {
    method: "POST",
    body: {
      email: input.email,
      purpose: input.purpose ?? "register",
    },
  });
}

export async function registerWithEmail(input: RegisterRequest): Promise<AuthSession> {
  const payload = await apiFetch<AuthSession>(ENDPOINTS.authRegister, {
    method: "POST",
    body: input,
  });

  return normalizeAuthSession(payload);
}

export async function loginWithPassword(input: LoginRequest): Promise<AuthSession> {
  const payload = await apiFetch<AuthSession>(ENDPOINTS.authLogin, {
    method: "POST",
    body: input,
  });

  return normalizeAuthSession(payload);
}

export async function changePassword(input: ChangePasswordRequest): Promise<void> {
  if (!apiState.token) {
    throw new ApiError("请先登录后再修改密码。", 401, "AUTH_REQUIRED");
  }

  await apiFetch(ENDPOINTS.authChangePassword, {
    method: "POST",
    auth: true,
    body: input,
  });
}

function normalizeAuthSession(payload: Partial<AuthSession> | null): AuthSession {
  if (!payload?.token || !payload.user?.id) {
    throw new Error("登录态返回异常，请重新登录");
  }

  return {
    token: payload.token,
    user: payload.user,
  };
}
